/**
 * The browser side of the SSE contract.
 *
 * A port of the stream reader in the Agri-Tech reference
 * (Agri_tech_ai_rag_files_to_copy/NewAIView.js:250-365), kept faithful to the
 * parts that were load-bearing there and rewritten where that file had grown
 * three competing branches for the same decision.
 *
 * What is preserved exactly:
 *
 *   - **The cross-chunk buffer.** A network chunk boundary lands in the middle
 *     of a JSON object roughly whenever the answer is long enough to matter.
 *     Parsing per-chunk instead of per-frame produces a stream that works in
 *     testing and drops tokens in production, which is the worst kind of bug.
 *
 *   - **Two separate token channels.** `delta.reasoning` is the model thinking;
 *     `delta.content` is the answer. They render in different places, so they
 *     are never concatenated — a reasoning token that leaks into the report is
 *     a paragraph of self-doubt printed above a clinical finding.
 *
 *   - **`<think>` tag handling.** Models that predate the `reasoning` field
 *     emit their thinking inline between tags. Both paths converge on the same
 *     two channels.
 *
 * What is rewritten: the reference's think-tag state machine had three
 * overlapping `if (msg.isThinking)` branches, two of them dead, and a comment
 * block reasoning about which one should win. The state here is one variable
 * with one transition table.
 *
 * `capsule` frames are ours, not OpenRouter's: the backend uses them to tell
 * the UI which model is being tried and which tier served the answer.
 */

import { API_BASE_URL, tokenStore } from '../../api/client';

/* ==========================================================================
   Parser
   ========================================================================== */

/**
 * A push parser for an OpenRouter-shaped SSE stream.
 *
 * Deliberately separate from the fetch call so it can be unit-tested by
 * feeding it byte-for-byte chunk splits — which is the only way to prove the
 * cross-chunk buffer works, and the reason the buffer bug is otherwise
 * invisible until production.
 *
 * @param {object} handlers
 * @param {(text: string) => void} [handlers.onContent]   answer tokens
 * @param {(text: string) => void} [handlers.onReasoning] thinking tokens
 * @param {(meta: object) => void} [handlers.onMeta]      capsule status frames
 * @param {(message: string) => void} [handlers.onError]  upstream error object
 * @param {() => void} [handlers.onDone]                  the [DONE] sentinel
 */
export function createStreamParser(handlers = {}) {
  const { onContent, onReasoning, onMeta, onError, onDone } = handlers;

  let buffer = '';
  let done = false;

  /**
   * Are we between `<think>` and `</think>`?
   *
   * The single piece of state in the whole parser. The reference kept two
   * overlapping flags for this and then needed a comment to work out which one
   * won; with one variable the question does not arise. Models using the
   * `reasoning` field never open a tag, so for them this stays false and every
   * content token routes straight to the answer channel — which is correct.
   */
  let insideThinkTag = false;

  /**
   * Route answer text, honouring any `<think>` boundaries inside it.
   *
   * A single token can carry both sides of a boundary ("...done</think>Now the
   * answer"), so this walks the string rather than testing it — splitting on
   * the first tag only would silently file the rest under the wrong channel.
   */
  function handleContent(content) {
    let rest = content;

    while (rest.length > 0) {
      if (!insideThinkTag) {
        const open = rest.indexOf('<think>');
        if (open === -1) {
          onContent?.(rest);
          return;
        }
        if (open > 0) onContent?.(rest.slice(0, open));
        insideThinkTag = true;
        rest = rest.slice(open + '<think>'.length);
      } else {
        const close = rest.indexOf('</think>');
        if (close === -1) {
          onReasoning?.(rest);
          return;
        }
        if (close > 0) onReasoning?.(rest.slice(0, close));
        insideThinkTag = false;
        rest = rest.slice(close + '</think>'.length);
      }
    }
  }

  /** Route one already-parsed data object. */
  function handleData(data) {
    // Our own side channel. Checked first: it shares the frame shape with a
    // model delta and would otherwise fall through to the content branch.
    if (data.capsule) {
      onMeta?.(data.capsule);
      return;
    }

    if (data.error) {
      const message =
        (typeof data.error === 'string' && data.error) ||
        data.error.message ||
        'حدث خطأ غير متوقع في محرك التحليل';
      onError?.(message);
      return;
    }

    const delta = data.choices?.[0]?.delta;

    // Reasoning arrives on its own field on the models that support it. It is
    // handled and returned immediately: a frame carrying reasoning never also
    // carries content, and treating the two as exclusive is what keeps the
    // channels from interleaving.
    if (delta?.reasoning) {
      onReasoning?.(delta.reasoning);
      return;
    }

    const content = delta?.content ?? data.message?.content;
    if (content) handleContent(content);
  }

  /** Consume one complete SSE frame (which may hold several `data:` lines). */
  function handleFrame(frame) {
    for (const rawLine of frame.split('\n')) {
      const line = rawLine.trim();
      if (!line || line.startsWith(':')) continue;

      const payload = line.startsWith('data:') ? line.slice(5).trim() : line;
      if (!payload) continue;

      if (payload === '[DONE]') {
        done = true;
        onDone?.();
        continue;
      }

      let data;
      try {
        data = JSON.parse(payload);
      } catch {
        // A genuinely malformed line, not a fragment — fragments never reach
        // here because the buffer holds them. Dropping it is correct.
        continue;
      }

      handleData(data);
    }
  }

  return {
    /** Feed decoded text. Safe to call with any chunking, including per-byte. */
    push(text) {
      buffer += text;

      // Frames are separated by a blank line. Anything after the last blank
      // line is incomplete and stays in the buffer until the next chunk.
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';

      for (const frame of frames) handleFrame(frame);
    },

    /** Call once the network stream ends, to consume a frame with no trailer. */
    flush() {
      if (buffer.trim()) {
        handleFrame(buffer);
        buffer = '';
      }
    },

    get finished() {
      return done;
    },
  };
}

/* ==========================================================================
   Transport
   ========================================================================== */

/**
 * POST a request and stream the reply.
 *
 * Not routed through `api/client.js` `request()` on purpose: that helper reads
 * the whole body with `response.text()` before returning, which is exactly the
 * blocking behaviour this feature exists to avoid. The token and base URL are
 * borrowed from it so there is still one place that knows where the API is and
 * how it is authenticated.
 *
 * @returns {Promise<{content: string, reasoning: string, meta: object, aborted: boolean}>}
 */
export async function streamAi(endpoint, body, { signal, onContent, onReasoning, onMeta, onError } = {}) {
  const token = tokenStore.get();

  let content = '';
  let reasoning = '';
  let meta = {};
  let errorMessage = null;

  const parser = createStreamParser({
    onContent: (text) => {
      content += text;
      onContent?.(text, content);
    },
    onReasoning: (text) => {
      reasoning += text;
      onReasoning?.(text, reasoning);
    },
    onMeta: (frame) => {
      meta = { ...meta, ...frame };
      onMeta?.(frame, meta);
    },
    onError: (message) => {
      errorMessage = message;
      onError?.(message);
    },
  });

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch (networkError) {
    if (networkError.name === 'AbortError') {
      return { content, reasoning, meta, aborted: true };
    }
    throw new Error(`تعذر الاتصال بالخادم: ${networkError.message}`);
  }

  if (!response.ok) {
    // The stream endpoints answer 4xx with JSON, not SSE — a refusal that the
    // request never got far enough to deliver in-band.
    let detail = `HTTP ${response.status}`;
    try {
      const parsed = await response.json();
      detail = parsed.error || parsed.message || detail;
    } catch {
      /* not JSON; the status is all there is */
    }
    throw new Error(detail);
  }

  if (!response.body) {
    throw new Error('المتصفح لا يدعم البث المباشر للاستجابة');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      if (value) parser.push(decoder.decode(value, { stream: true }));
    }
    parser.flush();
  } catch (readError) {
    if (readError.name === 'AbortError') {
      return { content, reasoning, meta, aborted: true };
    }
    throw readError;
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* already released by the abort */
    }
  }

  return { content, reasoning, meta, aborted: false, error: errorMessage };
}

/* ==========================================================================
   Report shaping
   ========================================================================== */

/**
 * The severity markers the prompt contract mandates.
 *
 * These are not decoration. They are the only reliable way to tell a report
 * line from everything else a model might emit, which is what `parseReport`
 * below depends on.
 */
export const SEVERITY = {
  '🔴': 'critical',
  '🟠': 'warning',
  '🟡': 'minor',
  '🔵': 'gap',
  '✅': 'action',
  '🩺': 'summary',
  '⚠️': 'notice',
};

const MARKERS = Object.keys(SEVERITY);
const markerOf = (line) => MARKERS.find((emoji) => line.startsWith(emoji)) || null;

/* --------------------------------------------------------------------------
   Inline markdown
-------------------------------------------------------------------------- */

/**
 * Split a line into styled segments: `**bold**`, `` `code` ``, plain.
 *
 * Models reach for markdown whether or not you ask, and the report template
 * itself uses `**الخلاصة:**`. Printing the asterisks literally is what made the
 * exported PDF look like a raw chat log rather than a document.
 *
 * Deliberately not a markdown library: this needs two inline forms across
 * ten lines, and a parser small enough to read is worth more here than one
 * that also handles reference links.
 */
export function parseInline(text) {
  if (!text) return [];

  const segments = [];
  // Bold first, then code — bold is the form the prompt contract actually uses,
  // and nesting beyond one level does not occur in a ten-line report.
  const pattern = /\*\*(.+?)\*\*|`([^`]+?)`/g;
  let cursor = 0;
  let match;

  while ((match = pattern.exec(text))) {
    if (match.index > cursor) {
      segments.push({ text: text.slice(cursor, match.index), bold: false, code: false });
    }
    if (match[1] !== undefined) segments.push({ text: match[1], bold: true, code: false });
    else segments.push({ text: match[2], bold: false, code: true });
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), bold: false, code: false });
  }

  return segments.filter((segment) => segment.text !== '');
}

/** Markdown stripped to plain text — for the clipboard and the print title. */
export const stripInline = (text) =>
  parseInline(text)
    .map((segment) => segment.text)
    .join('');

/* --------------------------------------------------------------------------
   The report
-------------------------------------------------------------------------- */

function toFinding(line, index) {
  const marker = markerOf(line);
  const tone = marker ? SEVERITY[marker] : 'neutral';
  const body = marker ? line.slice(marker.length).trim() : line;

  // "finding — likely cause". The em dash is the contract; a hyphen is what a
  // model reaches for when it forgets, so both are accepted.
  const split = body.split(/\s+—\s+|\s+-\s+/);
  const claim = split[0] || body;
  const cause = split.length > 1 ? split.slice(1).join(' — ') : null;

  return {
    id: `${index}-${tone}`,
    tone,
    marker,
    text: body,
    claim,
    cause,
    claimSegments: parseInline(claim),
    causeSegments: cause ? parseInline(cause) : null,
  };
}

/**
 * Separate the report from everything else the model said.
 *
 * **This is the fix for a real incident.** A reasoning model on the fallback
 * ladder (`nemotron-…-reasoning`) emits its entire chain of thought as plain
 * `delta.content` — no `<think>` tags, no `delta.reasoning` field — so the
 * stream parser upstream has nothing to quarantine it by. The exported PDF was
 * five pages of the model talking to itself in English ("We need to produce up
 * to 10 lines total… Let's count lines:") with the Arabic report buried inside
 * it, and a tail of degenerated tokens after it.
 *
 * The defence is to stop trusting the stream and trust the *contract* instead:
 * the prompt mandates that every report line opens with a severity marker, so
 * a line without one is not report content, wherever it appears. Preamble and
 * postamble are routed to `aside` for the reasoning drawer.
 *
 * The fallback matters as much as the rule. When the model emits no markers at
 * all, dropping everything would leave an empty panel with no explanation — so
 * `followedFormat: false` is reported and the raw text is handed back for the
 * UI to surface as a failed run the doctor can retry, rather than silently
 * rendering thinking as findings.
 */
export function parseReport(text) {
  if (!text) return { findings: [], aside: [], followedFormat: true, raw: '' };

  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const marked = lines.filter((line) => markerOf(line));

  if (marked.length === 0) {
    return {
      findings: [],
      aside: lines,
      followedFormat: false,
      raw: text.trim(),
    };
  }

  return {
    findings: marked.map(toFinding),
    aside: lines.filter((line) => !markerOf(line)),
    followedFormat: true,
    raw: text.trim(),
  };
}

/**
 * The findings alone.
 *
 * Kept as the narrow entry point for callers that only render the report.
 * A model that emits no markers still yields readable lines here rather than
 * nothing, because an empty list is indistinguishable from "no findings" —
 * `parseReport().followedFormat` is what tells those two apart.
 */
export function parseReportLines(text) {
  const report = parseReport(text);
  if (report.followedFormat) return report.findings;
  return report.aside.map(toFinding);
}

/** The line budget the prompt enforces. Shown in the UI as a live counter. */
export const MAX_REPORT_LINES = 10;
